import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Copy,
  History,
  Play,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip'
import { useGalgameController } from '../hooks/use-galgame-controller'
import { useEngineStore } from '../stores/use-engine-store'
import type { UIMessage } from '../types/chat-model'
import { ChatActionButton } from './chat-action-button'
import { ChatSettingsTrigger } from './chat-settings'
interface GalgameToolsProps {
  sessionId: string
}

export function GalgameTools({ sessionId }: GalgameToolsProps) {
  const { messages: messagesSlice, mode } = useEngineStore()
  const messages = messagesSlice.messages
  
  const {
    jumpToNextMessage,
    jumpToLastMessage,
    nextStep,
    lastStep,
    galgameIndex,
    galgameSegments,
    currentMessageIndex,
  } = useGalgameController()

  const canGoLastSegment = galgameIndex > 0
  const canGoNextSegment = galgameIndex < galgameSegments.length - 1
  const canGoLastMessage = currentMessageIndex > 0
  const canGoNextMessage = messages
    ? currentMessageIndex < messages.length - 1
    : false

  // 只在查看最后一条消息时显示操作按钮
  const isLastMessage = messages
    ? currentMessageIndex === messages.length - 1
    : false

  const toolButtonClass =
    'h-8 w-8 text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-20 transition-all'

  return (
    <TooltipProvider delayDuration={300}>
      <div className='flex items-center gap-0.5 rounded-lg border border-white/5 bg-black/20 p-1 backdrop-blur-sm'>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className={toolButtonClass}
              disabled={!canGoLastMessage}
              onClick={(e) => {
                e.stopPropagation()
                jumpToLastMessage()
              }}
            >
              <ChevronsLeft className='size-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent className='border-white/20 bg-black/80 text-xs text-white'>
            上一条消息
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className={toolButtonClass}
              disabled={!canGoLastMessage && !canGoLastSegment}
              onClick={(e) => {
                e.stopPropagation()
                lastStep()
              }}
            >
              <ChevronLeft className='size-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent className='border-white/20 bg-black/80 text-xs text-white'>
            上一句
          </TooltipContent>
        </Tooltip>

        <div className='mx-1 h-3 w-px bg-white/10' />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className={toolButtonClass}
              disabled={!canGoNextMessage && !canGoNextSegment}
              onClick={(e) => {
                e.stopPropagation()
                nextStep()
              }}
            >
              <ChevronRight className='size-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent className='border-white/20 bg-black/80 text-xs text-white'>
            下一句
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className={toolButtonClass}
              disabled={!canGoNextMessage}
              onClick={(e) => {
                e.stopPropagation()
                jumpToNextMessage()
              }}
            >
              <ChevronsRight className='size-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent className='border-white/20 bg-black/80 text-xs text-white'>
            下一条消息
          </TooltipContent>
        </Tooltip>

        <div className='mx-1 h-3 w-px bg-white/10' />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className={toolButtonClass}
              onClick={(e) => {
                e.stopPropagation()
                mode.setMode('chat')
              }}
            >
              <History className='size-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent className='border-white/20 bg-black/80 text-xs text-white'>
            查看历史记录
          </TooltipContent>
        </Tooltip>

        {isLastMessage && currentMessageIndex !== 0 && (
          <>
            <div className='mx-1 h-3 w-px bg-white/10' />

            <ChatActionButton sessionId={sessionId} />

            <div className='mx-1 h-3 w-px bg-white/10' />
          </>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <ChatSettingsTrigger />
          </TooltipTrigger>
          <TooltipContent className='border-white/20 bg-black/80 text-xs text-white'>
            设置
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  )
}

/**
 * 回溯按钮：在聊天模式的消息列表悬浮显示
 * 点击后切换为 Galgame 模式并跳转到指定消息
 */
export function GoToMessageButton({ msgIndex }: { msgIndex: number }) {
  const { mode } = useEngineStore()
  const { initSegments } = useGalgameController()
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            className='text-muted-foreground hover:bg-primary/10 hover:text-primary size-6 transition-all opacity-100'
            onClick={(e) => {
              e.stopPropagation()

              initSegments(msgIndex)
              mode.setMode('galgame')

            }}
          >
            <Play className='size-3 fill-current' />
          </Button>
        </TooltipTrigger>
        <TooltipContent className='text-[10px]'>
          从此消息开始播放
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/**
 * 复制消息按钮：复制消息内容和选项到剪贴板
 */
export function CopyMessageButton({ message }: { message: UIMessage }) {
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()

    // 构建复制内容
    let copyContent = message.content || ''

    // 如果有选项，添加选项文本（使用大写英文字母 A, B, C... 作为序号）
    if (message.choices && message.choices.length > 0) {
      const choicesText = message.choices
        .map((c, i) => `${String.fromCharCode(65 + i)}. ${c.text}`)
        .join('\n')
      copyContent += '\n\n' + choicesText
    }

    try {
      await navigator.clipboard.writeText(copyContent)
      toast.success('已复制到剪贴板')
    } catch {
      toast.error('复制失败')
    }
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            className='text-muted-foreground hover:bg-primary/10 hover:text-primary size-6 transition-all opacity-100'
            onClick={handleCopy}
          >
            <Copy className='size-3' />
          </Button>
        </TooltipTrigger>
        <TooltipContent className='text-[10px]'>
          复制消息内容
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

